import React, { useEffect, useState } from "react";
import cx from "classnames";
import Button from "components/Button/Button";
import { SeatMapLegend } from "components/SeatingChart/SeatMapLegend";
import { renderSeatingChartGeometry } from "components/SeatingChart/SeatingChartGeometry";
import importedStyles from "./AssignedSeatingPicker.module.css";
import Modal from "components/Modal/Modal";
import {
  ArrowLeftIcon,
  ExclamationCircleIcon,
  TicketIcon,
  XIcon,
} from "@heroicons/react/outline";
import {
  AssignedSeatingMap,
  AssignedSeatSelection,
  AssignedSeatingTicketType,
  getAssignedSeatPosition,
  getAssignedSeatTicketTypeColor,
  getSeatHoldRemainingSeconds,
  selectedAssignedSeatTotal,
} from "../models/assignedSeating";
import {
  adjustSeatingMapZoom,
  buildSeatingMapGeometry,
  formatSeatingHoldCountdown,
} from "./seatingPickerUtils";
import { getDiscountedEventLevelPrice } from "../eventPricing";

const styles: Record<string, string> =
  typeof importedStyles === "undefined" ? {} : importedStyles;

interface AssignedSeatingPickerProps {
  isOpen: boolean;
  isLoading: boolean;
  isHolding?: boolean;
  seatMap?: AssignedSeatingMap | null;
  ticketTypes?: AssignedSeatingTicketType[];
  selectedSeats: AssignedSeatSelection[];
  promotionCode?: any;
  eventLevels?: { levelid?: string; seatingTicketTypeKey?: string }[];
  currencySymbol?: string;
  eventName?: string;
  seatHoldExpiresAt?: string;
  conflict?: boolean;
  translate: (key: string) => string;
  onClose: () => void;
  onSeatToggle: (seatKey: string) => void;
  onSeatRemove: (seatKey: string) => void;
  onBuy: () => void;
  onRetry: () => void;
}

const seatClassName = ({
  state,
  selected,
}: {
  state: string;
  selected: boolean;
}) => {
  if (selected) {
    return cx(styles.seat, styles.seatSelected);
  }
  if (state === "available") {
    return cx(styles.seat, styles.seatAvailable);
  }
  return cx(styles.seat, styles.seatUnavailable);
};

const seatStateLabel = (state: string, translate: (key: string) => string) =>
  state === "available" ? translate("available") : translate("soldOut");

const matchingTicketType = (
  seat: AssignedSeatingMap["seats"][number],
  ticketTypes?: AssignedSeatingTicketType[]
) => {
  if (seat.type !== "seat") {
    return undefined;
  }
  return ticketTypes?.find(
    (ticketType) => ticketType.key === seat.ticketTypeKey
  );
};

const selectedSeatLegendMarkerStyle = (selectedSeatColors: string[]) => {
  if (selectedSeatColors.length === 1) {
    return { backgroundColor: selectedSeatColors[0] };
  }
  if (selectedSeatColors.length > 1) {
    return {
      background: `linear-gradient(90deg, ${selectedSeatColors.join(", ")})`,
    };
  }
  return undefined;
};

interface SeatMapInteractionProps {
  zoomPercent?: number;
  className?: string;
}

export const SeatMap = ({
  seatMap,
  ticketTypes,
  selectedSeats,
  onSeatToggle,
  translate,
  isHolding = false,
  zoomPercent = 100,
  className,
}: Pick<
  AssignedSeatingPickerProps,
  | "seatMap"
  | "ticketTypes"
  | "selectedSeats"
  | "onSeatToggle"
  | "translate"
  | "isHolding"
> &
  SeatMapInteractionProps) => {
  const selectedSeatKeys = new Set(selectedSeats.map((seat) => seat.seatKey));
  const seats = seatMap?.seats || [];
  const selectedSeatColors = Array.from(
    new Set(
      seats
        .filter((seat) => selectedSeatKeys.has(seat.seatKey))
        .map(
          (seat) =>
            matchingTicketType(seat, ticketTypes)?.color ||
            getAssignedSeatTicketTypeColor(seat)
        )
        .filter((color): color is string => Boolean(color))
    )
  );
  const { columns, rows, positions, rowLabels } =
    buildSeatingMapGeometry(seats);
  const seatPitch = 32;
  const rowLabelWidth = 20;
  const seatAreaWidth = columns * seatPitch;
  const seatsByRow = new Map<number, typeof seats>();

  seats.forEach((seat) => {
    const position = positions.get(seat.seatKey);
    if (!position) {
      return;
    }

    const rowSeats = seatsByRow.get(position.y) || [];
    rowSeats.push(seat);
    seatsByRow.set(position.y, rowSeats);
  });

  const geometryRows = Array.from({ length: rows }, (_, rowIndex) => {
    const y = rowIndex + 1;

    return {
      key: y,
      value: { label: rowLabels.get(y) },
      seats: (seatsByRow.get(y) || []).map((seat) => ({
        key: seat.seatKey,
        column: (positions.get(seat.seatKey)?.x || 1) + 1,
        value: seat,
      })),
    };
  });

  return (
    <div
      className={cx(styles.mapViewport, className)}
      data-testid="assigned-seating-map"
    >
      <div className={styles.mapCanvas} style={{ zoom: `${zoomPercent}%` }}>
        {renderSeatingChartGeometry({
          rows: geometryRows,
          columnCount: columns,
          seatWidth: seatPitch,
          rowRailWidth: rowLabelWidth,
          columnGap: 0,
          rowGap: 8,
          minHeight: rows * seatPitch,
          containerProps: { className: styles.mapContainer },
          containerDataAttributes: {
            "data-testid": "assigned-seating-map-canvas",
          },
          renderRowRail: ({ row, side }) =>
            row.value.label ? (
              <span
                className={cx(
                  styles.rowLabel,
                  side === "left" && styles.rowLabelLeft
                )}
              >
                {row.value.label}
              </span>
            ) : null,
          renderSeat: ({ seat: geometrySeat }) => {
            const seat = geometrySeat.value;
            const selected = selectedSeatKeys.has(seat.seatKey);
            const available = seat.state === "available";
            const { row, number } = getAssignedSeatPosition(seat);
            const matchedTicketType = matchingTicketType(seat, ticketTypes);
            const ticketType =
              seat.type === "seat"
                ? matchedTicketType?.name || seat.ticketTypeKey
                : undefined;
            const ticketTypeColor =
              matchedTicketType?.color || getAssignedSeatTicketTypeColor(seat);
            const label = [
              translate("row"),
              row,
              translate("seat"),
              number,
              ticketType,
              seatStateLabel(seat.state, translate),
            ]
              .filter(Boolean)
              .join(", ");

            return (
              <button
                type="button"
                data-seat-key={seat.seatKey}
                aria-label={label}
                title={label}
                aria-pressed={selected}
                disabled={(!available && !selected) || isHolding}
                className={seatClassName({
                  state: seat.state,
                  selected,
                })}
                style={{
                  ...(ticketTypeColor && (available || selected)
                    ? selected
                      ? {
                          borderColor: ticketTypeColor,
                          backgroundColor: ticketTypeColor,
                        }
                      : { borderColor: ticketTypeColor }
                    : {}),
                }}
                onClick={() => onSeatToggle(seat.seatKey)}
              >
                {number}
              </button>
            );
          },
          front: (
            <div style={{ marginLeft: rowLabelWidth, width: seatAreaWidth }}>
              <div className={styles.screen}>
                {translate(
                  seatMap?.screen.label === "stage"
                    ? "seatingFrontElementStage"
                    : "seatingFrontElementScreen"
                )}
              </div>
              <div
                className={styles.mapLegend}
                data-testid="assigned-seating-map-legend"
              >
                <div className={styles.mapLegendItems}>
                  <SeatMapLegend
                    variant="buyer"
                    translate={translate}
                    selectedMarkerTestId="assigned-seating-legend-selected-marker"
                    selectedMarkerStyle={selectedSeatLegendMarkerStyle(
                      selectedSeatColors
                    )}
                  />
                </div>
              </div>
            </div>
          ),
        })}
      </div>
    </div>
  );
};

const TicketTypeCards = ({
  ticketTypes,
  currencySymbol,
  translate,
}: Pick<
  AssignedSeatingPickerProps,
  "ticketTypes" | "currencySymbol" | "translate"
>) => {
  return (
    <section
      className={styles.ticketTypeSection}
      aria-labelledby="assigned-seating-ticket-types-heading"
      data-testid="assigned-seating-ticket-types"
    >
      <h2
        id="assigned-seating-ticket-types-heading"
        className={styles.sectionHeading}
      >
        {translate("seatingTicketTypes")}
      </h2>
      {ticketTypes?.length ? (
        <div className={styles.ticketTypeList}>
          {ticketTypes.map((ticketType) => (
            <div key={ticketType.key} className={styles.ticketTypeCard}>
              <div className={styles.ticketTypeInfo}>
                {ticketType.color ? (
                  <span
                    aria-hidden="true"
                    className={styles.ticketTypeMarker}
                    style={{ backgroundColor: ticketType.color }}
                  />
                ) : null}
                <span className={styles.ticketTypeName}>
                  {ticketType.name || ticketType.key}
                </span>
              </div>
              <div className={styles.ticketTypeMeta}>
                {Number.isFinite(ticketType.price) ? (
                  <span className={styles.ticketTypePrice}>
                    {currencySymbol}
                    {ticketType.price}
                  </span>
                ) : null}
                <span className={styles.ticketTypeAvailability}>
                  {ticketType.availableSeats} {translate("seats")}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export const SelectionSummary = ({
  selectedSeats,
  currencySymbol,
  onSeatRemove,
  translate,
  isHolding = false,
}: Pick<
  AssignedSeatingPickerProps,
  | "selectedSeats"
  | "currencySymbol"
  | "onSeatRemove"
  | "translate"
  | "isHolding"
>) => {
  return (
    <section className={styles.selectionSummary} aria-live="polite">
      <h3 className={styles.sectionHeading}>
        {translate("yourSelection")} ({selectedSeats.length})
      </h3>
      {selectedSeats.length ? (
        <>
          <ul className={styles.selectionList}>
            {selectedSeats.map((seat) => (
              <li key={seat.seatKey} className={styles.selectionItem}>
                {seat.ticketTypeColor ? (
                  <span
                    aria-hidden="true"
                    className={styles.selectedSeatMarker}
                    style={{ backgroundColor: seat.ticketTypeColor }}
                  />
                ) : null}
                <span className={styles.selectedSeatDescription}>
                  <span className={styles.selectedSeatTitle}>
                    {translate("row")} {seat.row} · {translate("seat")}{" "}
                    {seat.number}
                  </span>
                  {seat.ticketType ? (
                    <span className={styles.selectedSeatType}>
                      {seat.ticketType}
                    </span>
                  ) : null}
                </span>
                <span className={styles.selectedSeatPrice}>
                  {currencySymbol}
                  {seat.price}
                </span>
                <button
                  type="button"
                  className={styles.removeSeat}
                  aria-label={`${translate("remove")} ${translate("row")} ${
                    seat.row
                  } ${translate("seat")} ${seat.number}`}
                  disabled={isHolding}
                  onClick={() => onSeatRemove(seat.seatKey)}
                >
                  <XIcon className={styles.removeSeatIcon} />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
};

const MapZoomControls = ({
  onReset,
  onZoomOut,
  onZoomIn,
  translate,
}: {
  onReset: () => void;
  onZoomOut: () => void;
  onZoomIn: () => void;
  translate: (key: string) => string;
}) => (
  <div
    className={styles.zoomControls}
    data-testid="assigned-seating-zoom-controls"
  >
    <button
      type="button"
      className={cx(styles.zoomControl, styles.zoomControlWithText)}
      aria-label={translate("seatingZoomIn")}
      onClick={onZoomIn}
    >
      +
    </button>
    <button
      type="button"
      className={cx(styles.zoomControl, styles.zoomControlWithText)}
      aria-label={translate("seatingZoomOut")}
      onClick={onZoomOut}
    >
      −
    </button>
    <button
      type="button"
      className={styles.zoomControl}
      aria-label={translate("seatingZoomReset")}
      onClick={onReset}
    >
      <img
        alt=""
        className={styles.zoomControlIcon}
        src="/img/icons/seating-editor/reset_zoom.svg"
      />
    </button>
  </div>
);

const AssignedSeatingPicker = ({
  isOpen,
  isLoading,
  isHolding,
  seatMap,
  ticketTypes,
  selectedSeats,
  promotionCode,
  eventLevels,
  currencySymbol,
  eventName,
  seatHoldExpiresAt,
  conflict,
  translate,
  onClose,
  onSeatToggle,
  onSeatRemove,
  onBuy,
  onRetry,
}: AssignedSeatingPickerProps) => {
  const [zoomPercent, setZoomPercent] = useState(100);
  const [now, setNow] = useState(() => Date.now());
  const remainingSeconds = getSeatHoldRemainingSeconds(seatHoldExpiresAt, now);
  const hasActiveHold = Boolean(seatHoldExpiresAt && remainingSeconds > 0);
  const discountedTicketTypes = ticketTypes?.map((ticketType) => {
    const level = eventLevels?.find(
      (candidate) => candidate.seatingTicketTypeKey === ticketType.key
    );
    if (!level?.levelid || typeof ticketType.price === "undefined") {
      return ticketType;
    }
    return {
      ...ticketType,
      price: getDiscountedEventLevelPrice(
        { levelid: level.levelid, price: ticketType.price },
        promotionCode
      ),
    };
  });
  const discountedSelectedSeats = selectedSeats.map((seat) => ({
    ...seat,
    price: getDiscountedEventLevelPrice(
      { levelid: seat.levelId, price: seat.price },
      promotionCode
    ),
  }));
  const selectedTotal = selectedAssignedSeatTotal(discountedSelectedSeats);

  useEffect(() => {
    if (!hasActiveHold) {
      return;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [hasActiveHold, seatHoldExpiresAt]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      showCloseBtn={false}
      widthType="xl"
      panelClassName={styles.modalPanel}
      position={styles.modalPosition}
      onClose={onClose}
    >
      <div
        className={styles.dialog}
        data-testid="assigned-seating-fullscreen-dialog"
      >
        <header className={styles.header}>
          <button
            type="button"
            className={styles.closeButton}
            aria-label={translate("close")}
            disabled={isHolding}
            onClick={onClose}
          >
            <ArrowLeftIcon className={styles.closeIcon} />
          </button>
          <div className={styles.headerContent}>
            <p className={styles.eventName}>
              {eventName || seatMap?.name || translate("seatingChart")}
            </p>
            <p className={styles.eventSubtitle}>
              {translate("seatingChart")} · {seatMap?.name || ""}
            </p>
          </div>
          {hasActiveHold ? (
            <p className={styles.holdStatus} aria-live="polite">
              {translate("reservedSeating")} ·{" "}
              {formatSeatingHoldCountdown(remainingSeconds)}
            </p>
          ) : null}
        </header>

        {conflict ? (
          <section
            className={styles.conflict}
            role="alert"
            data-testid="assigned-seating-conflict"
          >
            <div className={styles.conflictCard}>
              <div className={styles.conflictIconContainer}>
                <ExclamationCircleIcon className={styles.conflictIcon} />
              </div>
              <h2 className={styles.conflictTitle}>
                {translate("selectedSeatsUnavailable")}
              </h2>
              <p className={styles.conflictDescription}>
                {translate("seatingSeatConflictDescription")}
              </p>
              <Button
                className={styles.conflictRetryButton}
                type="primary"
                onClick={onRetry}
              >
                {translate("seatingRetrySeats")}
              </Button>
            </div>
          </section>
        ) : isLoading ? (
          <p className={styles.loading}>{translate("seatingLoadingSeats")}</p>
        ) : (
          <div className={styles.content}>
            <section className={styles.mapSection}>
              <div className={styles.mapArea}>
                <div className={styles.mapControls}>
                  <MapZoomControls
                    onReset={() => setZoomPercent(100)}
                    onZoomOut={() =>
                      setZoomPercent((current) =>
                        adjustSeatingMapZoom(current, "out")
                      )
                    }
                    onZoomIn={() =>
                      setZoomPercent((current) =>
                        adjustSeatingMapZoom(current, "in")
                      )
                    }
                    translate={translate}
                  />
                </div>
                <SeatMap
                  seatMap={seatMap}
                  ticketTypes={discountedTicketTypes}
                  selectedSeats={discountedSelectedSeats}
                  onSeatToggle={onSeatToggle}
                  translate={translate}
                  isHolding={isHolding}
                  zoomPercent={zoomPercent}
                  className={styles.map}
                />
              </div>
            </section>

            <aside className={styles.sidebar}>
              <TicketTypeCards
                ticketTypes={discountedTicketTypes}
                currencySymbol={currencySymbol}
                translate={translate}
              />
              <div
                className={styles.selectionGroup}
                data-testid="assigned-seating-selection-group"
              >
                <SelectionSummary
                  selectedSeats={discountedSelectedSeats}
                  currencySymbol={currencySymbol}
                  onSeatRemove={onSeatRemove}
                  translate={translate}
                  isHolding={isHolding}
                />
                {selectedSeats.length ? (
                  <Button
                    className={styles.buyButton}
                    type="danger"
                    icon={<TicketIcon className={styles.ticketIcon} />}
                    loading={isHolding}
                    onClick={onBuy}
                  >
                    {`${translate("buyFor")} ${
                      currencySymbol || ""
                    }${selectedTotal}`}
                  </Button>
                ) : null}
              </div>
            </aside>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AssignedSeatingPicker;
